/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { appSyncGraphQLApi } from '../../app/smorgAppSyncGraphQLApi';
import { AccountActivitySignal, ActivityObjectType } from '../../API';

export enum ServerStatusValue {
  OK = 'OK',
  Checking = 'Checking',
  Ready = 'Ready',
  NotReady = 'Not ready',
}

export interface StaleResourceDetectionState {
  serverStatus: ServerStatusValue;
  activitySignals: Array<AccountActivitySignal>;
  activitySignalsOwnPublicIp: string | null;
}

const initialState: StaleResourceDetectionState = {
  serverStatus: ServerStatusValue.OK,
  activitySignals: [],
  activitySignalsOwnPublicIp: null,
};

export const serverStatusSlice = createSlice({
  name: 'serverStatus',
  initialState,
  reducers: {
    serverStatusUpdated: (state, action: PayloadAction<ServerStatusValue>) => {
      const newStatus = action.payload;
      state.serverStatus = newStatus;
    },
    activitySignalsAvailable: (
      state,
      action: PayloadAction<Array<AccountActivitySignal>>,
    ) => {
      state.activitySignals = action.payload;
    },
    activitySignalsOwnPublicIpAvailable: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.activitySignalsOwnPublicIp = action.payload;
    },
  },
  selectors: {
    /**
     * At most one user.
     */
    otherUserActiveOnResourceSelector: (
      state,
      myID: string,
      activityObjectType: ActivityObjectType,
      activityObjectID: string,
    ) => {
      // console.log({ activityObjectID, activityObjectType });
      const ownIp = state.activitySignalsOwnPublicIp;
      const relevantSignals = state.activitySignals.filter(
        (s) =>
          s.lastActivityObjectType === activityObjectType &&
          s.lastActivityObjectID === activityObjectID &&
          (s.owner !== myID || s.sourceIp !== ownIp),
      );
      // console.log(`Found ${relevantSignals.length} signals`);
      if (relevantSignals.length > 0) {
        const relevantSignal = relevantSignals[0];
        return relevantSignal.friendlyUsername || 'Another user';
      }
      return null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(appSyncGraphQLApi.util.resetApiState, (state) => {
      state.serverStatus = ServerStatusValue.OK;
      state.activitySignals = [];
      state.activitySignalsOwnPublicIp = null;
    });
  },
});

export const {
  serverStatusUpdated,
  activitySignalsAvailable,
  activitySignalsOwnPublicIpAvailable,
} = serverStatusSlice.actions;

export const { otherUserActiveOnResourceSelector } =
  serverStatusSlice.selectors;

export default serverStatusSlice.reducer;
